import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Input,
  Modal,
  RadioGroup,
  SearchInput,
  Spinner,
  Table,
  type TableColumn,
} from "../ds";
import { api, errorText, type Device, type Firmware } from "../lib/api";
import { nextNight, toLocalInput } from "../lib/format";

// Fleet-Update in einem Dialog: ein Image, viele Router, ein Zeitpunkt. Der
// Einzeltermin bleibt auf der Router-Seite (System → Firmware) — hier geht es
// um die Welle, und darum vorher zu sehen, WER überhaupt einen alten Stand hat.
//
// Angelegt werden ganz normale Einzeltermine (ein POST je Router). Kein
// Sammel-Endpunkt: der Server kennt nur „ein Router, ein Termin", und ein
// zweiter Weg dorthin wäre eine zweite Stelle mit denselben Prüfungen.

/** Ein Router mit dem, was für die Entscheidung „flashen oder nicht" zählt. */
interface Row {
  device: Device;
  /** Für diesen Router steht schon ein Termin — der Server lehnt einen zweiten ab. */
  planned: boolean;
  /** Läuft schon auf dem Bau-Stempel des gewählten Images. */
  current: boolean;
}

/** Vom Server abgewiesener Router, mit Begründung. */
interface Rejected {
  id: string;
  name: string;
  error: string;
}

// Der Serializer liefert "" statt null, wenn ein Router seinen Hostnamen noch
// nicht gemeldet hat — `??` griffe darauf nicht und die Zelle bliebe leer.
const deviceName = (d: Device) => d.hostname || d.id;

/** Spiegelt `core/firmware.py::check_variant`: eine Box auf einer gestempelten
 *  Variante (`eulektro-…`) verträgt nur Images derselben Variante — ein
 *  generic-Image würfe sie aus der Fleet-Verwaltung. Boxen ohne Stempel bleiben
 *  wählbar; verbindlich prüft ohnehin der Server. */
function sameVariant(device: Device, image: Firmware | null): boolean {
  if (!image || !device.imageBuild) return true;
  return device.imageBuild.split("-")[0] === (image.build || "").split("-")[0];
}

function deviceColumns(
  rows: Row[],
  selected: Set<string>,
  toggle: (id: string) => void,
  toggleAll: () => void,
): TableColumn<Row>[] {
  const selectable = rows.filter((r) => !r.planned);
  const allPicked = selectable.length > 0 && selectable.every((r) => selected.has(r.device.id));
  return [
    {
      key: "pick",
      width: 40,
      header: (
        <Checkbox
          label=""
          showBorder={false}
          aria-label="Alle auswählen"
          checked={allPicked}
          disabled={selectable.length === 0}
          onChange={toggleAll}
        />
      ),
      cell: (r) => (
        <Checkbox
          label=""
          showBorder={false}
          aria-label={deviceName(r.device)}
          checked={selected.has(r.device.id)}
          disabled={r.planned}
          onChange={() => toggle(r.device.id)}
        />
      ),
    },
    {
      key: "device",
      header: "Router",
      cell: (r) => (
        <span>
          {deviceName(r.device)}
          <span className="block text-xs text-content-3">{r.device.model || "—"}</span>
        </span>
      ),
    },
    { key: "firmware", header: "Aktuelles Image", cell: (r) => r.device.firmwareVersion || "—" },
    {
      key: "build",
      header: "Eulektro-Build",
      // Die OpenWrt-Version allein sagt nicht, welcher interne Stand läuft —
      // zwischen zwei Builds derselben Version liegen Agent- und Paketstände.
      cell: (r) => (
        <span className="flex flex-wrap items-center gap-2">
          {r.device.imageBuild || "—"}
          {r.current && <Badge variant="success">aktuell</Badge>}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      align: "right",
      cell: (r) =>
        r.planned ? (
          <Badge variant="info" dot>
            geplant
          </Badge>
        ) : r.device.online ? (
          <Badge variant="success" dot>
            online
          </Badge>
        ) : (
          <Badge variant="neutral" dot>
            offline
          </Badge>
        ),
    },
  ];
}

/** Nur signierte Images stehen zur Wahl: unsignierte nimmt der Server beim
 *  Anlegen des Termins ohnehin nicht an (`schedule.create`). */
function ImagePicker({
  images,
  value,
  onChange,
}: {
  images: Firmware[];
  value: string | null;
  onChange: (id: string) => void;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-content-2">Image</h3>
      {images.length === 0 ? (
        <Alert variant="warning">
          Kein signiertes Image im Katalog. Ohne usign-Signatur flasht der Router nicht — erst unter
          „Images &amp; Modelle" hochladen.
        </Alert>
      ) : (
        <RadioGroup
          value={value}
          onChange={onChange}
          showBorder
          options={images.map((f) => ({
            value: f.id,
            label: (
              <span className="flex flex-wrap items-center gap-2">
                {f.version}
                {f.tag && <Badge variant="info">{f.tag}</Badge>}
                <span className="text-xs text-content-3">{f.model}</span>
              </span>
            ),
            description: f.build || "ohne Bau-Stempel",
          }))}
        />
      )}
    </section>
  );
}

function DevicePicker({
  rows,
  selected,
  filter,
  onFilter,
  toggle,
  toggleAll,
}: {
  rows: Row[];
  selected: Set<string>;
  filter: string;
  onFilter: (value: string) => void;
  toggle: (id: string) => void;
  toggleAll: () => void;
}) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-content-2">
          Router{selected.size > 0 && ` — ${selected.size} gewählt`}
        </h3>
        <SearchInput value={filter} onChange={onFilter} placeholder="Router filtern…" />
      </div>
      <Table
        columns={deviceColumns(rows, selected, toggle, toggleAll)}
        rows={rows}
        rowKey={(r) => r.device.id}
        emptyMessage="Kein Router passt zum Filter."
      />
    </section>
  );
}

/** Termine anlegen — nacheinander, nicht parallel: 50 gleichzeitige POSTs
 *  bringen nichts, und wenn einer scheitert, soll danebenstehen, welcher. */
async function createSchedules(
  targets: Device[],
  firmwareId: string,
  runAt: string,
  onProgress: (done: number) => void,
): Promise<Rejected[]> {
  const rejected: Rejected[] = [];
  for (const [i, device] of targets.entries()) {
    onProgress(i + 1);
    try {
      await api.scheduleFlash(device.id, firmwareId, runAt);
    } catch (e) {
      rejected.push({ id: device.id, name: deviceName(device), error: errorText(e) });
    }
  }
  return rejected;
}

/** Zeitpunkt für die ganze Welle. Ortszeit rein, ISO mit Zone raus — der
 *  Server kennt sonst nur die Ziffern auf der Uhr. */
function TimePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <section className="flex flex-col gap-2">
      <Input
        type="datetime-local"
        label="Zeitpunkt"
        value={value}
        min={toLocalInput(new Date())}
        helperText="Ortszeit, für alle gewählten Router derselbe. Der Server arbeitet sie nacheinander ab — jeder Flash dauert ein paar Minuten."
        onChange={(e) => onChange(e.target.value)}
      />
      <Alert variant="warning" title="Kein Rollback">
        Jeder Router ist danach einige Minuten offline. Vor dem Flashen holt der Server ein
        Konfigurations-Backup; kommt es nicht an, wird der Router nicht geflasht.
      </Alert>
    </section>
  );
}

function RejectedAlert({ rejected }: { rejected: Rejected[] }) {
  return (
    <Alert variant="danger" title={`${rejected.length} Termin(e) abgelehnt`}>
      <ul className="list-disc pl-4">
        {rejected.map((r) => (
          <li key={r.id}>
            {r.name}: {r.error}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

function PlanFooter({
  count,
  progress,
  ready,
  onClose,
  onPlan,
}: {
  count: number;
  /** Wie viele Termine schon durch sind — `null`, solange nichts läuft. */
  progress: number | null;
  ready: boolean;
  onClose: () => void;
  onPlan: () => void;
}) {
  const running = progress !== null;
  return (
    <>
      <Button variant="ghost" onClick={onClose} disabled={running}>
        Abbrechen
      </Button>
      <Button
        variant="primary"
        iconName="timer"
        disabled={!ready}
        loading={running}
        onClick={onPlan}
      >
        {running
          ? `${progress}/${count}`
          : `${count || ""} Termin${count === 1 ? "" : "e"} setzen`.trim()}
      </Button>
    </>
  );
}

export function FleetFlashModal({
  plannedDeviceIds,
  onClose,
  onDone,
}: {
  /** Router mit offenem Termin. Ein zweiter wäre ein Rennen um dieselbe Partition. */
  plannedDeviceIds: Set<string>;
  onClose: () => void;
  /** Termine angelegt — die Liste dahinter ist veraltet. */
  onDone: () => void;
}) {
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [images, setImages] = useState<Firmware[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [firmwareId, setFirmwareId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [runAt, setRunAt] = useState(nextNight);
  const [openedAt] = useState(() => Date.now());
  const [progress, setProgress] = useState<number | null>(null);
  const [failed, setFailed] = useState<Rejected[]>([]);

  useEffect(() => {
    Promise.all([api.listDevices(), api.listFirmware()])
      .then(([d, f]) => {
        setDevices(d);
        setImages(f);
      })
      .catch((e: unknown) => {
        setDevices([]);
        setImages([]);
        setError(errorText(e));
      });
  }, []);

  const flashable = useMemo(() => (images ?? []).filter((f) => f.signed), [images]);
  const image = flashable.find((f) => f.id === firmwareId) ?? null;

  const { rows, hiddenCount } = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const all = (devices ?? [])
      .map((device) => ({
        device,
        planned: plannedDeviceIds.has(device.id),
        current: !!image?.build && device.imageBuild === image.build,
      }))
      .filter(
        (r) =>
          !needle ||
          `${deviceName(r.device)} ${r.device.model ?? ""}`.toLowerCase().includes(needle),
      )
      .sort((a, b) => deviceName(a.device).localeCompare(deviceName(b.device)));
    const shown = all.filter((r) => sameVariant(r.device, image));
    return { rows: shown, hiddenCount: all.length - shown.length };
  }, [devices, filter, image, plannedDeviceIds]);

  // Beim Image-Wechsel fliegen Häkchen auf jetzt ausgeblendeten Routern raus —
  // eine unsichtbare Auswahl zählte sonst im Fuß weiter mit.
  function pickImage(id: string) {
    setFirmwareId(id);
    const chosen = flashable.find((f) => f.id === id) ?? null;
    setSelected((old) => {
      const kept = [...old].filter((sel) => {
        const device = (devices ?? []).find((d) => d.id === sel);
        return device !== undefined && sameVariant(device, chosen);
      });
      return kept.length === old.size ? old : new Set(kept);
    });
  }

  function toggle(id: string) {
    setSelected((old) => {
      const next = new Set(old);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  // „Alle" meint die gerade sichtbaren: wer nach „Camiko" filtert und alle
  // anhakt, meint die Camikos — nicht die halbe Flotte dahinter.
  function toggleAll() {
    const selectable = rows.filter((r) => !r.planned).map((r) => r.device.id);
    setSelected((old) => {
      const next = new Set(old);
      if (selectable.every((id) => next.has(id))) selectable.forEach((id) => next.delete(id));
      else selectable.forEach((id) => next.add(id));
      return next;
    });
  }

  const parsed = new Date(runAt);
  const timeOk = runAt !== "" && !Number.isNaN(parsed.getTime()) && parsed.getTime() > openedAt;
  const ready = !!image && selected.size > 0 && timeOk && progress === null;

  async function plan() {
    if (!image) return;
    setFailed([]);
    const targets = rows.filter((r) => selected.has(r.device.id)).map((r) => r.device);
    const rejected = await createSchedules(targets, image.id, parsed.toISOString(), setProgress);
    setProgress(null);
    onDone();
    if (rejected.length === 0) return onClose();
    // Was durchging, verliert sein Häkchen — sonst legt ein zweiter Klick alles
    // noch einmal an. Stehen bleibt, was der Server abgelehnt hat.
    setFailed(rejected);
    setSelected(new Set(rejected.map((r) => r.id)));
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Flash planen"
      subtitle="Ein Image, mehrere Router, ein Zeitpunkt. Der Server fährt die Termine nacheinander."
      size="xl"
      actions={
        <PlanFooter
          count={selected.size}
          progress={progress}
          ready={ready}
          onClose={onClose}
          onPlan={() => void plan()}
        />
      }
    >
      <div className="flex flex-col gap-4">
        {error && <Alert variant="danger">{error}</Alert>}
        {failed.length > 0 && <RejectedAlert rejected={failed} />}

        {devices === null || images === null ? (
          <div className="flex justify-center p-8">
            <Spinner />
          </div>
        ) : (
          <>
            <ImagePicker images={flashable} value={firmwareId} onChange={pickImage} />
            {hiddenCount > 0 && (
              <Alert variant="info">
                {hiddenCount} Router ausgeblendet: sie laufen eine andere Image-Variante als das
                gewählte Image — ein Wechsel würde sie aus der Fleet-Verwaltung werfen.
              </Alert>
            )}
            <DevicePicker
              rows={rows}
              selected={selected}
              filter={filter}
              onFilter={setFilter}
              toggle={toggle}
              toggleAll={toggleAll}
            />
            <TimePicker value={runAt} onChange={setRunAt} />
          </>
        )}
      </div>
    </Modal>
  );
}
